import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import BlogMainTab, { slugify } from '../components/blog/BlogMainTab';
import BlogSEOTab from '../components/blog/BlogSEOTab';
import BlogSocialTab from '../components/blog/BlogSocialTab';
import BlogAssistantTab from '../components/blog/BlogAssistantTab';
import { Save, ArrowLeft, Loader2, FileText, Search, Share2, Sparkles } from 'lucide-react';

const emptyForm = {
  title: '',
  slug: '',
  content: '',
  featured_image: '',
  featured_image_alt: '',
  category: '',
  hashtags: [],
  status: 'draft',
  seo_title: '',
  seo_description: '',
  focus_keyword: '',
  is_indexable: true,
  og_title: '',
  og_description: '',
  author_display: '',
  show_views: true,
  show_comments: true,
  enable_rich_results: true,
};

const TABS = [
  { id: 'main', label: 'Main', icon: FileText },
  { id: 'seo', label: 'SEO', icon: Search },
  { id: 'social', label: 'Social', icon: Share2 },
  { id: 'assistant', label: 'Assistant', icon: Sparkles },
];

const BlogEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('main');
  const [slugTouched, setSlugTouched] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const isEditing = !!slug;

  useEffect(() => {
    if (isEditing) {
      fetchBlog();
    } else if (user) {
      setFormData((prev) => ({ ...prev, author_display: user.name }));
    }
  }, [slug, user]);

  // Auto-suggest slug from title until the user edits the slug manually
  useEffect(() => {
    if (!isEditing && !slugTouched) {
      setFormData((prev) => ({ ...prev, slug: slugify(prev.title) }));
    }
  }, [formData.title, slugTouched, isEditing]);

  const fetchBlog = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/blogs/${slug}`, { credentials: 'include' });
      if (response.ok) {
        const blog = await response.json();
        setFormData({
          title: blog.title || '',
          slug: blog.slug || '',
          content: blog.content || '',
          featured_image: blog.featured_image || '',
          featured_image_alt: blog.featured_image_alt || '',
          category: blog.category || '',
          hashtags: blog.hashtags || [],
          status: blog.status || 'draft',
          seo_title: blog.seo_title || '',
          seo_description: blog.seo_description || '',
          focus_keyword: blog.focus_keyword || '',
          is_indexable: blog.is_indexable !== false,
          og_title: blog.og_title || '',
          og_description: blog.og_description || '',
          author_display: blog.author_display || '',
          show_views: blog.show_views !== false,
          show_comments: blog.show_comments !== false,
          enable_rich_results: blog.enable_rich_results !== false,
        });
        setSlugTouched(true);
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.featured_image) {
      alert('Please add a Featured Image before saving.');
      setActiveTab('main');
      return;
    }
    setSaving(true);
    try {
      const url = isEditing ? `${API_URL}/api/blogs/${slug}` : `${API_URL}/api/blogs`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const blog = await response.json();
        navigate(`/blog/${blog.slug}`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save blog');
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Please sign in to write a blog</p>
          <button onClick={() => navigate('/')} data-testid="blog-editor-go-home-btn" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => navigate(-1)} data-testid="blog-editor-back-btn" className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
              <ArrowLeft size={20} className="mr-2" />Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}</h1>
            <div className="w-20" />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="flex border-b border-gray-200 overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    data-testid={`blog-editor-tab-${tab.id}`}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
                      activeTab === tab.id ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <tab.icon size={16} />{tab.label}
                  </button>
                ))}
              </div>
              <div className="p-6">
                {activeTab === 'main' && <BlogMainTab formData={formData} setFormData={setFormData} onSlugEdit={() => setSlugTouched(true)} />}
                {activeTab === 'seo' && <BlogSEOTab formData={formData} setFormData={setFormData} />}
                {activeTab === 'social' && <BlogSocialTab formData={formData} setFormData={setFormData} />}
                {activeTab === 'assistant' && <BlogAssistantTab formData={formData} setFormData={setFormData} />}
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button type="button" onClick={() => navigate(-1)} data-testid="blog-editor-cancel-btn" className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="blog-editor-submit-btn"
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <><Loader2 size={20} className="animate-spin mr-2" />Saving...</>
                ) : (
                  <><Save size={20} className="mr-2" />{isEditing ? 'Update Post' : (formData.status === 'draft' ? 'Save Draft' : 'Publish Post')}</>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogEditor;
